
import VueApp from '@/@types/app/VueApp';
import { ArticleEditMode } from '@/@types/ArticleEditMode';
import { RecordingReaction } from '@/@types/RecordingReaction';
import { WorkspaceViewArticleItem } from '@/@types/WorkspaceViewArticleItem';
import { WorkspaceOrder } from '@/@types/WorkspaceOrder';
import EditorNavbar from '@/components/article-editor/EditorNavbar/EditorNavbar.vue';
import ArticlePlayer from '@/components/article-player/ArticlePlayer.vue';
import VideoReactionMenu from '@/components/article-player/VideoReactionMenu.vue';
import ArticleVersionsFooter from '@/components/ArticleVersionsFooter/ArticleVersionsFooter.vue';
import EmptyStateCard from '@/components/layout/cards/EmptyStateCard.vue';
import ArticlesMergeDialog from '@/components/layout/dialogs/ArticlesMergeDialog.vue';
import ArticleTagsDialog from '@/components/layout/dialogs/ArticleTagsDialog.vue';
import ArticleTransferDialog from '@/components/layout/dialogs/ArticleTransferDialog.vue';
import SubArticleCreateDialog from '@/components/layout/dialogs/SubArticleCreateDialog.vue';
import ArticleGroupDialog from '@/components/layout/dialogs/ArticleGroupDialog.vue';
import EventBus from '@/EventBus';
import { ARTICLE_CREATE, CHECK_PARAGRAPH_PROGRESS, PARAGRAPH_PROGRESS_UPDATE } from '@/events';
import helper from '@/helper';
import { Article } from '@/models/article/Article';
import { Paragraph } from '@/models/article/Paragraph';
import { Company } from '@/models/Company';
import { User } from '@/models/User';
import { Workspace } from '@/models/Workspace';
import { Logger } from '@/other/Logger';
import { getFixedNavbarWidth, syncPinnedNavbarWidth } from '@/other/fixedNavbarWidth';
import * as routerNames from '@/routerNames';
import apiService from '@/services/ApiService';
import articleService from '@/services/article/ArticleService';
import authService from '@/services/AuthService';
import mobileResponsiveService from '@/services/MobileResponsiveService';
import notificationService from '@/services/NotificationService';
import articlePlayerProvider from '@/services/ui-providers/ArticlePlayerProvider';
import urlService from '@/services/UrlService';
import { workspaceService } from '@/services/workspace/WorkspaceService';
import store from '@/store';
import { commitSetArticleEditorArticle } from '@/store/commits/articleEditorCommits';
import { commitSetArticleRecordingReactions } from '@/store/commits/commentsSidebarCommits';
import { commitSetArticleMode, commitSetAutoPlay } from '@/store/commits/sharedCommits';
import { commitClearDiffState } from '@/store/commits/diffCommits';
import { commitSetSelectedArticle } from '@/store/commits/workspaceViewCommits';
import { dispatchUpdateArticleMode } from '@/store/dispatchers/articleDispatchers';
import {
  dispatchCloseArticleVersionMode,
  dispatchResetArticleVersionView,
} from '@/store/dispatchers/articleVersionDispatchers';
import { dispatchDisableOcrMode } from '@/store/dispatchers/uiDispatchers';
import {
  dispatchWorkspaceViewPageMounted,
  dispatchWorkspaceViewSelectedArticleChanged,
} from '@/store/dispatchers/workspaceViewDispatchers';
import { Mutations } from '@/store/mutations';
import { Component, Prop, Watch } from 'vue-property-decorator';
import slugify from 'slugify';

const log = new Logger('WorkspaceViewPage');

@Component({
  components: {
    ArticleTransferDialog,
    SubArticleCreateDialog,
    ArticleGroupDialog,
    VideoReactionMenu,
    ArticleTagsDialog,
    EmptyStateCard,
    EditorNavbar,
    ArticlesMergeDialog,
    ArticlePlayer,
    ArticleTextEditor: () => import('@/components/article-editor/ArticleTextEditor.vue'),
    ArticleVersionsFooter,
  },
})
export default class WorkspaceViewPage extends VueApp {
  @Prop({ required: true }) readonly workspace: Workspace;
  @Prop(Article) readonly sharedArticle: Article;
  @Prop(String) textTabSlug: string;

  articles: Article[] = [];
  playerHeight = 0;
  private removeEditGuard: (() => void) | null = null;
  reorderDialogVisible = false;
  style = {};
  showNavbar = true;
  lastScrollPosition = 0;
  timeoutContainer: any = null;
  updateCurrentUrlTimeout: number = null;
  INIT_TIMEOUT_DURATION = 1000;
  maxMobileLandscapeHeight = 375;
  currentParagraphProgress = 0;
  availableCompanies: Company[] = [];

  $refs: {
    articleTransferDialog: ArticleTransferDialog;
    articlesMergeDialog: ArticlesMergeDialog;
    subArticleCreateDialog: SubArticleCreateDialog;
    articleGroupDialog: ArticleGroupDialog;
    workspaceContainer: HTMLDivElement;
    playerContainer: HTMLDivElement;
  };

  get navbarWidthFallback() {
    const isMainSidebarExpanded = store.state.ui.isMainSidebarExpanded;
    return this.isMobile ? 'calc(100% - 40px)' : `calc(100% - ${isMainSidebarExpanded ? 570 : 460}px)`;
  }

  setNavbarWidth() {
    return getFixedNavbarWidth(this.$refs.playerContainer, this.navbarWidthFallback);
  }

  get isNotesModePinned() {
    return !!this.$store.state.articleEditor.article?.pinnedNote && !this.$route.params.paragraphSlug;
  }

  get isMobileView() {
    return this.$store.state.isMobile;
  }

  get isOcrMode() {
    return this.$store.state.ui.isOcrModeEnabled;
  }

  get user(): User {
    return this.$store.state.user;
  }

  get isGuest(): boolean {
    return this.$auth.isGuest();
  }

  get selectedArticle() {
    return this.$store.state.workspaceView.selectedArticle;
  }

  get selectedParagraph(): Paragraph {
    return this.$store.state.workspaceView.selectedParagraph;
  }

  get articleMode() {
    return this.$store.state.articleMode;
  }

  get workspaceView() {
    return workspaceService.workspaceView;
  }

  get isMobile() {
    return mobileResponsiveService.isMobileView;
  }

  get userCanManageArticles() {
    return this.workspaceView.workspace.canEditArticles();
  }

  get windowInnerHeight() {
    return mobileResponsiveService.windowInnerHeight;
  }

  get autoPlay() {
    return this.$store.state.autoPlay;
  }

  get isSharedMode() {
    return this.workspaceView.isSharedMode;
  }

  get articleVersionView() {
    return this.$store.state.articleVersionView;
  }

  get nonArchivedArticles() {
    const articles = this.workspaceView.articles;
    let filteredArticles: any = [];
    if (articles.length) {
      filteredArticles = articles.filter((article) => article.isArchived !== true);
    }

    return filteredArticles ? filteredArticles.length : false;
  }

  get personalWorkspaceId() {
    return this.$store.state.personalWorkspace?.id;
  }

  get isAuthorized() {
    return authService.isAuthorized();
  }

  get isDiffModeActive(): boolean {
    return this.$store.state.diffMode?.active || false;
  }

  async init() {
    this.timeoutContainer = setTimeout(() => {
      const container = this.$refs.workspaceContainer;
      if (container) {
        container.addEventListener('scroll', this.onScroll);
      }
    }, this.INIT_TIMEOUT_DURATION);

    EventBus.$emit('toggle-sidebar', true);
    const autoPlayArticleId = this.$store.state.autoPlay ? this.$store.state.autoPlay.articleId : null;

    try {
      if (this.sharedArticle) {
        await dispatchUpdateArticleMode(this.sharedArticle);
        this.setActiveTextTab(true);
      }

      if (this.selectedArticle?.isArchived && autoPlayArticleId === this.selectedArticle.id) {
        commitSetAutoPlay(null);
        await this.$router.push(urlService.getWorkspaceViewRoute(this.workspace));
      }
    } catch (err) {
      log.error(err);
    }

    window.addEventListener('resize', () => {
      this.updatePlayerHeight();
      const navbar = document.querySelector('#player-navbar') as HTMLElement;
      syncPinnedNavbarWidth(navbar, this.$refs.playerContainer, this.navbarWidthFallback);
    });
    this.$nextTick(this.updatePlayerHeight);

    EventBus.$on('transfer-article', async (article: Article) => {
      await this.handleTransferArticle(article);
    });

    EventBus.$on('merge-article', async (article: WorkspaceViewArticleItem) => {
      await this.handleMergeArticle(article);
    });

    EventBus.$on('reorder-articles', () => {
      this.handleReorderArticles();
    });

    EventBus.$on('create-sub-article', async (article: WorkspaceViewArticleItem) => {
      await this.handleCreateSubArticle(article);
    });

    EventBus.$on('create-sub-article-in-group', async (groupNode: WorkspaceOrder) => {
      await this.handleCreateSubArticleInGroup(groupNode);
    });

    EventBus.$on('create-group', async (article: WorkspaceViewArticleItem) => {
      await this.handleCreateGroup(article);
    });

    EventBus.$on('rename-group', async (groupNode: WorkspaceOrder) => {
      await this.handleRenameGroup(groupNode);
    });

    EventBus.$on('ungroup', async (groupNode: WorkspaceOrder) => {
      await this.handleUngroup(groupNode);
    });

    // Auto scroll to video player
    if (window.innerHeight < this.maxMobileLandscapeHeight && this.isMobile && this.articleMode === 'video') {
      const container = this.$refs.workspaceContainer;
      const scrollOffset = 85;
      container.scrollTo({ top: scrollOffset, behavior: 'smooth' });
    }
  }

  beforeDestroy() {
    clearTimeout(this.timeoutContainer);
    EventBus.$off('reorder-articles');
    EventBus.$off('merge-article');
    EventBus.$off('create-group');
    EventBus.$off('create-sub-article-in-group');
    EventBus.$off('rename-group');
    EventBus.$off('ungroup');
    EventBus.$off(CHECK_PARAGRAPH_PROGRESS);
    commitSetSelectedArticle(null);
    commitClearDiffState();
    dispatchResetArticleVersionView();
    window.removeEventListener('resize', () => {
      this.updatePlayerHeight();
    });
    window.clearTimeout(this.updateCurrentUrlTimeout);
    dispatchDisableOcrMode();
    if (this.removeEditGuard) {
      this.removeEditGuard();
      this.removeEditGuard = null;
    }
  }

  // methods
  handlePlayerReady() {
    this.updatePlayerHeight(true);
  }

  handleParagraphPlaying(data: { paragraph: Paragraph; progress: number; paragraphTime: number }) {
    const { paragraph, progress } = data;

    if (
      (!this.selectedParagraph && paragraph) ||
      (this.selectedParagraph && paragraph && this.selectedParagraph?.id !== paragraph?.id)
    ) {
      workspaceService.setState('selectedParagraph', paragraph);
      if (this.articleMode === ArticleEditMode.VIDEO) {
        window.clearTimeout(this.updateCurrentUrlTimeout);
        this.updateCurrentUrlTimeout = window.setTimeout(() => {
          this.updateCurrentUrl(paragraph);
        }, 500);
      }
    }
    this.currentParagraphProgress = progress;
    EventBus.$emit(PARAGRAPH_PROGRESS_UPDATE, progress * 100);
  }

  async handleTransferArticle(article: Article) {
    try {
      const dialog = this.$refs.articleTransferDialog;
      if (dialog && typeof dialog.show === 'function') {
        await dialog.show(article, this.availableCompanies);
      }
    } catch (err) {
      if (!err) return;
      log.error(err);
    }
  }

  async handleMergeArticle(article: WorkspaceViewArticleItem) {
    try {
      await this.$refs.articlesMergeDialog.show(article);
    } catch (err) {
      if (!err) return;
      log.error(err);
    }
  }

  handleReorderArticles() {
    this.$store.commit(Mutations.SET_REORDER_MODE, true);
    this.reorderDialogVisible = true;
  }

  async handleCreateSubArticle(article: WorkspaceViewArticleItem) {
    try {
      const workspace = this.workspaceView.workspace;

      const showDialog = async () => {
        if (!this.$refs.subArticleCreateDialog) return;
        try {
          const item: Article = await this.$refs.subArticleCreateDialog.show(article, workspace);

          if (item instanceof Article) {
            const routeParams = item.id ? { id: item.id } : {};
            await this.$router.push({ name: routerNames.ARTICLE_CREATE, params: routeParams });
            if (routerNames.ARTICLE_CREATE !== this.$route.name) return;

            commitSetArticleEditorArticle(item);
            commitSetArticleMode(ArticleEditMode.NOTES);
            store.commit('setCreateArticle', item);
          }
        } catch (err) {
          apiService.handleResponseError(err);
        }
      };

      await showDialog();
    } catch (err) {
      if (!err) return;
      apiService.handleResponseError(err);
    }
  }

  async handleCreateSubArticleInGroup(groupNode: WorkspaceOrder) {
    try {
      const workspace = this.workspaceView.workspace;

      const showDialog = async () => {
        if (!this.$refs.subArticleCreateDialog) return;
        try {
          const item: Article = await this.$refs.subArticleCreateDialog.showForGroup(groupNode, workspace);

          if (item instanceof Article) {
            const routeParams = item.id ? { id: item.id } : {};
            await this.$router.push({ name: routerNames.ARTICLE_CREATE, params: routeParams });
            if (routerNames.ARTICLE_CREATE !== this.$route.name) return;

            commitSetArticleEditorArticle(item);
            commitSetArticleMode(ArticleEditMode.NOTES);
            store.commit('setCreateArticle', item);
          }
        } catch (err) {
          apiService.handleResponseError(err);
        }
      };

      await showDialog();
    } catch (err) {
      if (!err) return;
      apiService.handleResponseError(err);
    }
  }

  // Group Operations
  generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  async handleCreateGroup(article: WorkspaceViewArticleItem) {
    try {
      const groupName = await this.$refs.articleGroupDialog.show('create');

      if (!groupName) return;

      const workspace = this.workspaceView.workspace;
      const workspaceOrder = workspace.order;

      // Generate unique group ID
      const groupId = `group_${this.generateUUID()}`;

      // Find article in tree and replace with group
      const result = this.findAndReplaceArticleWithGroup(workspaceOrder, article.id, groupId, groupName);

      if (result) {
        workspace.order = result;

        // Save to backend
        try {
          await workspaceService.saveArticlesOrder(workspace.order, false);
          notificationService.success('Group created successfully');

          // Navigate to the article
          if (article && workspace.slug) {
            const articleSlug = slugify(article.title);
            const url = `/b/${workspace.shortId}/${workspace.slug}/${article.shortId}/${articleSlug}`;
            this.$router.replace(url);
          }
        } catch (err) {
          log.error('Error saving group:', err);
          notificationService.error('Failed to create group');
        }
      }
    } catch (err) {
      // Dialog was cancelled
      if (!err) return;
      log.error('Error creating group:', err);
    }
  }

  async handleRenameGroup(groupNode: WorkspaceOrder) {
    try {
      const groupName = await this.$refs.articleGroupDialog.show('rename', groupNode.title);

      if (!groupName || groupName === groupNode.title) return;

      const workspace = this.workspaceView.workspace;
      const workspaceOrder = workspace.order;

      // Find and update group title
      const result = this.findAndUpdateGroupTitle(workspaceOrder, groupNode.value, groupName);

      if (result) {
        workspace.order = result;

        // Save to backend
        try {
          await workspaceService.saveArticlesOrder(workspace.order, false);
          notificationService.success('Group renamed successfully');
        } catch (err) {
          log.error('Error renaming group:', err);
          notificationService.error('Failed to rename group');
        }
      }
    } catch (err) {
      // Dialog was cancelled
      if (!err) return;
      log.error('Error renaming group:', err);
    }
  }

  async handleUngroup(groupNode: WorkspaceOrder) {
    try {
      const workspace = this.workspaceView.workspace;
      const workspaceOrder = workspace.order;

      // Find and flatten group
      const result = this.findAndFlattenGroup(workspaceOrder, groupNode.value);

      if (result) {
        workspace.order = result;

        // Save to backend
        try {
          await workspaceService.saveArticlesOrder(workspace.order);
          notificationService.success('Group dissolved successfully');
        } catch (err) {
          log.error('Error ungrouping:', err);
          notificationService.error('Failed to ungroup');
        }
      }
    } catch (err) {
      log.error('Error ungrouping:', err);
      notificationService.error('Failed to ungroup');
    }
  }

  // Utility functions for group operations
  findAndReplaceArticleWithGroup(
    order: WorkspaceOrder[],
    articleId: string,
    groupId: string,
    groupName: string
  ): WorkspaceOrder[] | null {
    const newOrder = [...order];

    for (let i = 0; i < newOrder.length; i++) {
      if (newOrder[i].value === articleId) {
        // Found the article - replace with group containing the article
        newOrder[i] = {
          value: groupId,
          type: 'group',
          title: groupName,
          items: [{ ...newOrder[i] }],
        };
        return newOrder;
      }

      // Recursively search in nested items
      if (newOrder[i].items && newOrder[i].items.length > 0) {
        const result = this.findAndReplaceArticleWithGroup(newOrder[i].items, articleId, groupId, groupName);
        if (result) {
          newOrder[i] = { ...newOrder[i], items: result };
          return newOrder;
        }
      }
    }

    return null;
  }

  findAndUpdateGroupTitle(order: WorkspaceOrder[], groupId: string, newTitle: string): WorkspaceOrder[] | null {
    const newOrder = [...order];

    for (let i = 0; i < newOrder.length; i++) {
      if (newOrder[i].value === groupId && newOrder[i].type === 'group') {
        // Found the group - update title
        newOrder[i] = { ...newOrder[i], title: newTitle };
        return newOrder;
      }

      // Recursively search in nested items
      if (newOrder[i].items && newOrder[i].items.length > 0) {
        const result = this.findAndUpdateGroupTitle(newOrder[i].items, groupId, newTitle);
        if (result) {
          newOrder[i] = { ...newOrder[i], items: result };
          return newOrder;
        }
      }
    }

    return null;
  }

  findAndFlattenGroup(order: WorkspaceOrder[], groupId: string): WorkspaceOrder[] | null {
    const newOrder = [...order];

    for (let i = 0; i < newOrder.length; i++) {
      if (newOrder[i].value === groupId && newOrder[i].type === 'group') {
        // Found the group - flatten it recursively
        const flattenedItems = this.flattenGroupRecursive(newOrder[i].items);
        // Replace group with flattened items
        newOrder.splice(i, 1, ...flattenedItems);
        return newOrder;
      }

      // Recursively search in nested items
      if (newOrder[i].items && newOrder[i].items.length > 0) {
        const result = this.findAndFlattenGroup(newOrder[i].items, groupId);
        if (result) {
          newOrder[i] = { ...newOrder[i], items: result };
          return newOrder;
        }
      }
    }

    return null;
  }

  flattenGroupRecursive(items: WorkspaceOrder[]): WorkspaceOrder[] {
    const flattened: WorkspaceOrder[] = [];

    for (const item of items) {
      if (item.type === 'group') {
        // Recursively flatten nested groups
        flattened.push(...this.flattenGroupRecursive(item.items));
      } else {
        // Keep articles as-is (preserving sub-articles)
        flattened.push(item);
      }
    }

    return flattened;
  }

  findFirstArticleInGroup(groupNode: WorkspaceOrder): WorkspaceViewArticleItem | null {
    if (!groupNode.items || groupNode.items.length === 0) {
      return null;
    }

    for (const item of groupNode.items) {
      if (item.type === 'group') {
        // Recursively search in nested group
        const article = this.findFirstArticleInGroup(item);
        if (article) return article;
      } else {
        // Found an article
        const article = this.workspaceView.articles.find((a) => a.id === item.value);
        if (article) return article;
      }
    }

    return null;
  }

  handleNavigateToFirstArticle(payload: { groupNode: WorkspaceOrder; newTab: boolean }) {
    const { groupNode, newTab } = payload;
    const firstArticle = this.findFirstArticleInGroup(groupNode);

    if (firstArticle) {
      const workspace = this.workspaceView.workspace;

      if (workspace && workspace.slug) {
        // Generate article slug from title since WorkspaceViewArticleItem doesn't have slug property
        const articleSlug = slugify(firstArticle.title);
        const url = `#/b/${workspace.shortId}/${workspace.slug}/${firstArticle.shortId}/${articleSlug}`;

        if (newTab) {
          window.open(url, '_blank');
        } else {
          this.$router.push(url);
        }
      }
    }
  }

  handleVideoDurationDetected(data: any) {
    const { video, duration } = data;
    if (this.workspaceView.selectedArticle) {
      this.workspaceView.selectedArticle.updateVideoDuration(video, duration);
      helper.observerNotify(this.workspaceView.selectedArticle.paragraphs);
    }
  }

  createArticle() {
    this.$store.commit('setWorkspace', this.workspaceView.workspace);
    EventBus.$emit(ARTICLE_CREATE);
  }

  updatePlayerHeight(fixUpdate = false) {
    if (this.$refs.playerContainer) {
      this.playerHeight = (this.$refs.playerContainer as HTMLElement).clientHeight;
    } else if (!fixUpdate) {
      setTimeout(() => {
        if (this && this.$refs.playerContainer) {
          this.updatePlayerHeight(true);
        }
      }, 500);
    }
  }

  async updateCurrentUrl(paragraph: Paragraph) {
    if (!paragraph || this.sharedArticle) return;

    const currentRouteParams = this.$route.params;
    const paragraphSlug = paragraph.getSlug();

    if (
      currentRouteParams.mode === ArticleEditMode.NOTES ||
      (this.isNotesModePinned && !currentRouteParams.paragraphSlug) ||
      !(!currentRouteParams.paragraphSlug || currentRouteParams.paragraphSlug !== paragraphSlug)
    ) {
      return;
    }

    const route = urlService.getParagraphViewRoute(
      currentRouteParams.paragraphSlug ? currentRouteParams.paragraphSlug : paragraphSlug,
      this.selectedArticle,
      this.$store.state.workspace
    );

    await this.$router.push(route);
  }

  onScroll() {
    const container = document.querySelector('.workspace-container') as HTMLElement;
    const navbar = document.querySelector('#player-navbar') as HTMLElement;
    const currentScrollPosition = container.scrollTop || document.documentElement.scrollTop;

    if (container.scrollTop > 140 && this.showNavbar) {
      navbar.style.position = 'fixed';
      navbar.style.width = this.setNavbarWidth();
    } else if (container.scrollTop === 0) {
      navbar.style.width = '100%';
      navbar.style.position = 'absolute';
    }

    if (Math.abs(currentScrollPosition - this.lastScrollPosition) < 65) {
      return;
    }
    this.showNavbar = currentScrollPosition < this.lastScrollPosition;
    this.lastScrollPosition = currentScrollPosition;
  }

  changeArticleMode(mode: ArticleEditMode) {
    return articleService.toggleArticleMode(mode, this.$route);
  }

  setActiveTextTab(isSharedArticle = false) {
    const article = isSharedArticle ? this.sharedArticle : this.selectedArticle;

    if (!this.textTabSlug && article?.pinnedNote) this.changeArticleMode(ArticleEditMode.NOTES);

    workspaceService.setActiveTextTab({
      article,
      textTabSlug: this.textTabSlug,
      isPinnedPriority: !this.textTabSlug,
    });
  }

  private async handleSelectedArticleChange(article?: Article, oldArticle?: Article) {
    if (article && oldArticle && article.id === oldArticle.id) return;

    // Clear diff mode only when switching between articles (oldArticle exists),
    // not on initial mount — BrowsePage may have already set diff state
    if (oldArticle) {
      commitClearDiffState();
    }

    const container = this.$refs.workspaceContainer;

    if (container) {
      container.scrollTop = 0;
    }

    if (this.selectedArticle) {
      if (!this.articleMode || !this.$route.params.paragraphSlug) {
        await dispatchUpdateArticleMode(this.selectedArticle);
      }
      this.$api.articleReactions.get(this.selectedArticle.id).then((res) => {
        const { reactions } = res.data;
        commitSetArticleRecordingReactions(reactions as unknown as RecordingReaction[]);
      });
    }

    if (article && oldArticle && article.id !== oldArticle.id && this.articleVersionView.isShowFooter) {
      await dispatchCloseArticleVersionMode();
    }

    this.showNavbar = true;
    await dispatchWorkspaceViewSelectedArticleChanged();

    if (this.articleMode === ArticleEditMode.NOTES) this.setActiveTextTab();
  }

  created() {
    const user = store.state.user;

    EventBus.$on(CHECK_PARAGRAPH_PROGRESS, () => {
      EventBus.$emit(PARAGRAPH_PROGRESS_UPDATE, this.currentParagraphProgress * 100);
    });

    if (store.state.userCompanies) {
      this.availableCompanies = store.state.userCompanies.filter((company: Pick<Company, 'id' | 'title'>) => {
        const companyId = typeof user.company === 'object' ? user.company.id : user.company;
        return company.id !== companyId;
      });
    }
  }

  async mounted() {
    await this.init();
    await dispatchWorkspaceViewPageMounted();
    await this.handleSelectedArticleChange(this.selectedArticle);

    // Auto-exit diff mode when navigating away (e.g., to Edit or AI mode)
    this.removeEditGuard = this.$router.beforeEach((to, from, next) => {
      if (this.isDiffModeActive) {
        commitClearDiffState();
      }
      next();
    });
  }

  @Watch('selectedArticle', { deep: true })
  async onSelectedArticleChanged(article: Article, oldArticle: Article) {
    await this.handleSelectedArticleChange(article, oldArticle);
  }

  @Watch('articleMode')
  onArticleModeChanged() {
    const videoPlayerRef = articlePlayerProvider.videoPlayer;
    if (videoPlayerRef) {
      videoPlayerRef.stop();
    }
    // timeout for waiting video on the page
    setTimeout(() => {
      this.updatePlayerHeight();
    });
  }

  @Watch('$route')
  onRouteChanged() {
    this.$store.dispatch('commentsSidebarCheckAutoShowComment');
  }
}
